import { useEffect, useRef, useState } from "react";
import {
  LifecycleError,
  LifecycleService,
} from "../../../services/Integrations/LifecycleService.ts";
import type {
  CanonicalPolicy,
  DocumentCapability,
  DocumentType,
  Schedule,
} from "../../../types/lifecycle.ts";
import { installmentKey, isPayable } from "./operations.ts";
import {
  paymentBlanks,
  useBlankInventory,
  validPaymentBlanks,
} from "./useBlankInventory.ts";
import { useBooking } from "./useBooking.ts";
import { useLifecycleOperations } from "./useLifecycleOperations.ts";
import { useLifecycleQuote } from "./useLifecycleQuote.ts";
import { useLifecycleRecords } from "./useLifecycleRecords.ts";
import { useLookup } from "./useLookup.ts";
export interface Notice {
  kind: "success" | "warning" | "error";
  text: string;
  detail?: string;
}

export function useLifecycle(jwt: string, account: string, headOff: string) {
  const quoteForm = useLifecycleQuote(jwt);
  const records = useLifecycleRecords(jwt, account, quoteForm);
  const { inputs, draft, quote, provider, stale, leasing } = quoteForm;
  const { operations, state: operationState } = useLifecycleOperations(account);
  const bookingForm = useBooking(jwt, provider, headOff);
  // Blanks live in wing-backend per insurer agency, so they can only be listed
  // once the operator has chosen the local INS_COMP.CID for this insurer.
  const blankForm = useBlankInventory(
    jwt,
    provider,
    operationState.inventoryRevision,
    false,
    bookingForm.cid
  );
  const [policy, setPolicy] = useState<CanonicalPolicy | null>(null);
  // A policy carries no local CID. Reuse the booking selection only when it is
  // for the same insurer; otherwise the picker falls back to free-text entry.
  const paymentBlankForm = useBlankInventory(
    jwt,
    policy?.provider ?? "",
    operationState.inventoryRevision,
    true,
    policy?.provider && policy.provider === provider ? bookingForm.cid : ""
  );
  const advisories = [
    ...operationState.issuedAdvisories,
    ...operationState.payments
      .filter(
        (payment) =>
          payment.response.policy.policyNumber === policy?.policyNumber
      )
      .flatMap((payment) => payment.response.advisories),
  ];
  const [documents, setDocuments] = useState<DocumentCapability[]>([]);
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [readBusy, setReadBusy] = useState<string | null>(null);
  const readLock = useRef(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const capabilities = useLookup(jwt, () =>
    LifecycleService.getCapabilities(jwt)
  );
  const current =
    capabilities.value?.find(
      (capability) => capability.provider === provider
    ) ?? null;
  const policyCapabilities = capabilities.value?.find(
    (capability) => capability.provider === policy?.provider
  );
  const can = (operation: keyof NonNullable<typeof current>["operations"]) =>
    policyCapabilities?.operations[operation]?.state === "supported";
  const selected =
    quote?.offers.find((offer) => offer.provider === provider) ?? null;
  const [signedOffer, setSignedOffer] = useState<{
    quote: NonNullable<typeof quote>;
    provider: typeof provider;
  } | null>(null);
  const offerSigned = Boolean(
    quote &&
      !stale &&
      signedOffer?.quote === quote &&
      signedOffer.provider === provider
  );
  const setOfferSigned = (checked: boolean) =>
    setSignedOffer(checked && quote ? { quote, provider } : null);
  useEffect(() => {
    setSignedOffer(null);
  }, [quote, provider]);
  const booking = selected?.booking ?? null;
  const busy = operationState.busy
    ? operationState.pending?.kind ?? "write"
    : readBusy;
  const policyNumber = policy?.policyNumber ?? "";
  const lesseeMissing = Boolean(
    leasing?.detected &&
      (!draft?.holder.pin || draft.holder.pin === leasing.ownerPin)
  );
  const driverDigits = (draft?.driver.pin ?? "").replace(/\D/g, "");
  const driverPinLooksWrong =
    driverDigits.length > 0 && driverDigits.length !== 10;
  const missingRequired = draft
    ? [
        ...(lesseeMissing
          ? [{ path: "holder.pin", label: "ЕГН / ЕИК на лизингополучателя" }]
          : []),
        ...(!(Number(draft.vehicle.maxWeightF1) > 0)
          ? [
              {
                path: "vehicle.maxWeightF1",
                label: "Технически допустима максимална маса (F1)",
              },
            ]
          : []),
      ]
    : [];
  const localBookingValid = Boolean(
    bookingForm.valid &&
      inputs.vehicleType &&
      inputs.purpose &&
      booking &&
      draft?.holder.pin &&
      (draft.holder.customerType === "legal"
        ? draft.holder.companyName
        : draft.holder.firstName || draft.holder.familyName)
  );
  const canIssue = Boolean(
    quote &&
      offerSigned &&
      !stale &&
      !quoteForm.hasPendingEdits &&
      selected?.canIssue &&
      !missingRequired.length &&
      !driverPinLooksWrong &&
      localBookingValid &&
      records.ready &&
      !operations.blocked &&
      !busy &&
      !blankForm.loading &&
      !blankForm.error
  );
  const fail = (failure: unknown) => {
    setError(failure);
    if (!(failure instanceof LifecycleError))
      setNotice({
        kind: "error",
        text: "Операцията не завърши успешно. Опитайте отново.",
        detail: failure instanceof Error ? failure.message : String(failure),
      });
  };
  const run = async (label: string, action: () => Promise<void>) => {
    if (readLock.current || operations.getSnapshot().busy) return;
    readLock.current = true;
    operations.clearError();
    setReadBusy(label);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (failure) {
      fail(failure);
    } finally {
      readLock.current = false;
      setReadBusy(null);
    }
  };
  useEffect(() => {
    setPolicy(operationState.issuedPolicy);
    if (operationState.issue) {
      quoteForm.clearQuote();
    }
    setSchedule(null);
    setDocuments([]);
  }, [operationState.issuedPolicy, operationState.issue]);
  const search = () =>
    run("quote", () => quoteForm.search(records.prepareQuote));
  const issue = () =>
    run("issue", async () => {
      if (!canIssue || !provider || !quote || !draft || !inputs.vehicleType)
        return;
      const previous = operations.getSnapshot().issue;
      const holder = draft.holder;
      const vehicle = draft.vehicle;
      await operations.issue(jwt, {
        provider,
        quoteHandle: quote.handle,
        quote: quote.priced,
        blanks: { ...blankForm.blanks, territoryId: "5" },
        localWrite: {
          cid: Number(bookingForm.cid),
          divisionId: Number(bookingForm.divisionId),
          agentId: Number(bookingForm.agentId),
          commissionPercentage: bookingForm.commissionNumber,
          zeroCommission: false,
          additionalData: bookingForm.additionalData,
          client: {
            id: records.parties.holder.id,
            pin: holder.pin,
            name:
              holder.customerType === "legal"
                ? holder.companyName
                : [holder.firstName, holder.surname, holder.familyName]
                    .filter(Boolean)
                    .join(" "),
            address: holder.street,
            phone: holder.phone || "",
            type: holder.customerType,
            country: "BG",
            settlementCode: holder.settlementCode,
          },
          vehicle: {
            id: records.vehicle.id,
            registrationNumber: inputs.plate.trim().toUpperCase(),
            certificateNumber: inputs.certNumber.trim(),
            chassis: vehicle.chassis,
            make: vehicle.make,
            model: vehicle.model,
            displacement: vehicle.displacement || undefined,
            enginePower: vehicle.enginePower || undefined,
            productionYear: vehicle.productionYear || undefined,
            color: vehicle.color || "",
            canonicalType: inputs.vehicleType,
            canonicalPurpose: inputs.purpose,
          },
        },
      });
      const result = operations.getSnapshot().issue;
      if (result && result !== previous) {
        quoteForm.clearQuote();
        setDocuments([]);
        setSchedule(null);
        setNotice({
          kind: "success",
          text: `Издадена полица ${result.policy.policyNumber}`,
        });
      }
    });
  const loadDocuments = () =>
    run("documents", async () => {
      setDocuments(
        (await LifecycleService.getDocumentIndex(jwt, policyNumber)).documents
      );
    });
  const loadSchedule = () =>
    run("schedule", async () => {
      setSchedule(
        (await LifecycleService.getSchedule(jwt, policyNumber)).schedule
      );
    });
  const openDocument = (type: DocumentType) =>
    run(`doc:${type}`, async () => {
      const blob = await LifecycleService.getDocument(jwt, policyNumber, type);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${policyNumber}-${type}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    });
  const paid = (row: Schedule["installments"][number]) =>
    Boolean(
      policy &&
        operationState.payments.some(
          (payment) => payment.key === installmentKey(policy, row)
        )
    );
  const canPay = (row: Schedule["installments"][number]) =>
    Boolean(
      policy &&
        can("installments.payment") &&
        isPayable(row) &&
        !paid(row) &&
        !operations.blocked &&
        !busy &&
        !paymentBlankForm.loading &&
        !paymentBlankForm.error &&
        validPaymentBlanks(policy.provider, paymentBlankForm.blanks)
    );
  const pay = (index: number) =>
    run(`pay:${index}`, async () => {
      const row = schedule?.installments[index];
      if (!policy || !row || !canPay(row)) return;
      await operations.pay(jwt, policy, row, {
        policyNumber,
        paymentTarget: row.paymentTarget,
        amount: { amount: row.amount.amount, currency: row.amount.currency },
        paymentMethod: "cash",
        blanks: paymentBlanks(policy.provider, paymentBlankForm.blanks),
      });
      const payment = operations
        .getSnapshot()
        .payments.find(
          (payment) => payment.key === installmentKey(policy, row)
        );
      if (!payment) return;
      setNotice({
        kind: "success",
        text: `Отчетено плащане ${payment.response.receipt.amount.eur.toFixed(
          2
        )} EUR — разписка ${payment.response.receipt.receiptNumber ?? "—"}`,
      });
      try {
        setSchedule(
          (await LifecycleService.getSchedule(jwt, policyNumber)).schedule
        );
      } catch {
        setNotice({
          kind: "warning",
          text: "Плащането е потвърдено. Списъкът с вноски не се обнови; заредете го отново.",
        });
      }
    });
  const displayedError = operationState.error ?? error;
  return {
    jwt,
    capabilities: capabilities.value ?? [],
    quoteForm,
    records,
    bookingForm,
    blankForm,
    paymentBlankForm,
    operations,
    operationState,
    ...inputs,
    draft,
    quote,
    provider,
    stale,
    leasing,
    selected,
    booking,
    current,
    busy,
    policy,
    policyNumber,
    advisories,
    documents,
    schedule,
    error: displayedError instanceof LifecycleError ? displayedError : null,
    notice,
    lookupError: capabilities.error || bookingForm.error,
    lesseeMissing,
    driverPinLooksWrong,
    missingRequired,
    canIssue,
    offerSigned,
    setOfferSigned,
    can,
    canPay,
    paid,
    search,
    issue,
    loadDocuments,
    loadSchedule,
    openDocument,
    pay,
  };
}
export type LifecycleController = ReturnType<typeof useLifecycle>;
